import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { discogsAPI, copiesAPI } from '../lib/api';
import { parseDiscogsTitle, CONDITION_OPTIONS, formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Disc3, ArrowLeft, Camera, Keyboard, X, Check, Loader2, 
  Package, Barcode, AlertCircle, Trash2, Plus, Zap, StopCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function BatchImport() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('camera'); // 'camera' or 'manual'
  const [scanning, setScanning] = useState(false);
  const [manualBarcode, setManualBarcode] = useState('');
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [defaultConditionVinyl, setDefaultConditionVinyl] = useState('VG+');
  const [defaultConditionCover, setDefaultConditionCover] = useState('VG+');
  const [lastScannedCode, setLastScannedCode] = useState(null);
  
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const readerRef = useRef(null);
  const scannedCodesRef = useRef(new Set());

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (readerRef.current) {
      readerRef.current = null;
    }
    setScanning(false);
  }, []);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  const searchByBarcode = async (barcode) => {
    // Prevent duplicate scans within short time
    if (scannedCodesRef.current.has(barcode)) {
      return;
    }
    scannedCodesRef.current.add(barcode);
    
    // Clear from set after 5 seconds to allow re-scanning
    setTimeout(() => {
      scannedCodesRef.current.delete(barcode);
    }, 5000);
    
    setLastScannedCode(barcode);
    setLoading(true);
    
    try {
      const res = await discogsAPI.search({ barcode });
      
      if (res.data.results?.length > 0) {
        const result = res.data.results[0];
        const { artist, title } = parseDiscogsTitle(result.title);
        
        // Check if already in queue
        if (queue.some(item => item.discogs_release_id === result.id)) {
          toast.info('Album already in queue');
          return;
        }
        
        // Fetch marketplace stats
        let marketStats = null;
        try {
          const statsRes = await discogsAPI.getRelease(result.id);
          marketStats = statsRes.data.marketplace_stats;
        } catch {
          // Stats not available
        }
        
        const queueItem = {
          id: `${result.id}-${Date.now()}`,
          discogs_release_id: result.id,
          artist: artist || 'Unknown Artist',
          title: title || result.title,
          year: result.year,
          label: result.label?.[0],
          format: result.format?.[0],
          country: result.country,
          cover_image: result.cover_image || result.thumb,
          condition_vinyl: defaultConditionVinyl,
          condition_cover: defaultConditionCover,
          market_value: marketStats?.median?.value,
          market_currency: marketStats?.median?.currency,
          barcode: barcode,
          release_data: {
            discogs_release_id: result.id,
            discogs_master_id: result.master_id,
            artist: artist || 'Unknown Artist',
            title: title || result.title,
            year: result.year,
            label: result.label?.[0],
            format: result.format?.[0],
            country: result.country,
            cover_image_url: result.cover_image,
            genres: result.genre || [],
            styles: result.style || [],
            market_value_median: marketStats?.median?.value,
            market_value_low: marketStats?.lowest_price?.value,
            market_value_high: marketStats?.highest_price?.value,
            market_value_currency: marketStats?.median?.currency,
          }
        };
        
        setQueue(prev => [...prev, queueItem]);
        toast.success(`Added: ${queueItem.artist} - ${queueItem.title}`);
        
        // Play success sound (optional)
        try {
          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleQ==');
          audio.volume = 0.3;
          audio.play().catch(() => {});
        } catch {}
        
      } else {
        toast.error(`No album found for barcode: ${barcode}`);
      }
    } catch (err) {
      if (err.response?.status === 503) {
        toast.error('Discogs not configured');
      } else if (err.response?.status === 403) {
        toast.error('Barcode scan limit reached. Upgrade to PRO.');
        stopCamera();
      } else {
        toast.error('Search failed');
      }
    } finally {
      setLoading(false);
      setLastScannedCode(null);
    }
  };

  const startCamera = async () => {
    try {
      setCameraError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setScanning(true);

      const { BrowserMultiFormatReader } = await import('@zxing/browser');
      const reader = new BrowserMultiFormatReader();
      readerRef.current = reader;

      reader.decodeFromVideoDevice(undefined, videoRef.current, async (result) => {
        if (result) {
          const barcode = result.getText();
          await searchByBarcode(barcode);
        }
      });

    } catch (err) {
      console.error('Camera error:', err);
      setCameraError('Could not access camera. Please use manual entry.');
      setMode('manual');
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!manualBarcode.trim()) return;
    await searchByBarcode(manualBarcode.trim());
    setManualBarcode('');
  };

  const removeFromQueue = (id) => {
    setQueue(prev => prev.filter(item => item.id !== id));
  };

  const updateQueueItemCondition = (id, field, value) => {
    setQueue(prev => prev.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const applyDefaultConditions = () => {
    setQueue(prev => prev.map(item => ({
      ...item,
      condition_vinyl: defaultConditionVinyl,
      condition_cover: defaultConditionCover
    })));
    toast.success('Applied default conditions to all items');
  };

  const handleImport = async () => {
    if (queue.length === 0) return;
    
    setImporting(true);
    stopCamera();
    
    try {
      const items = queue.map(item => ({
        discogs_release_id: item.discogs_release_id,
        condition_vinyl: item.condition_vinyl,
        condition_cover: item.condition_cover,
        release_data: item.release_data
      }));
      
      const res = await copiesAPI.batchAdd(items, defaultConditionVinyl, defaultConditionCover);
      
      if (res.data.added_count > 0) {
        toast.success(`Successfully added ${res.data.added_count} albums to collection!`);
      }
      
      if (res.data.error_count > 0) {
        toast.error(`${res.data.error_count} albums failed to import`);
      }
      
      navigate('/');
      
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error(err.response?.data?.detail || 'Copy limit reached');
      } else {
        toast.error('Import failed. Please try again.');
      }
    } finally {
      setImporting(false);
    }
  };

  const totalValue = queue.reduce((sum, item) => sum + (item.market_value || 0), 0);

  const placeholderImage = 'https://images.unsplash.com/photo-1642444525640-d3eb287ed389?w=80&h=80&fit=crop';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => { stopCamera(); navigate('/'); }}
              className="gap-2"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="gap-1">
                <Package className="w-3 h-3" />
                {queue.length} items
              </Badge>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Scanner Section */}
          <div className="space-y-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-primary" />
                  Batch Import
                </CardTitle>
                <CardDescription>
                  Scan multiple barcodes continuously. Albums are added to a queue for review before importing.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Mode Toggle */}
                <div className="flex gap-2">
                  <Button
                    variant={mode === 'camera' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setMode('camera'); if (!scanning) startCamera(); }}
                    className="gap-2"
                    data-testid="batch-camera-mode"
                  >
                    <Camera className="w-4 h-4" />
                    Camera
                  </Button>
                  <Button
                    variant={mode === 'manual' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setMode('manual'); stopCamera(); }}
                    className="gap-2"
                    data-testid="batch-manual-mode"
                  >
                    <Keyboard className="w-4 h-4" />
                    Manual
                  </Button>
                </div>

                {mode === 'camera' ? (
                  <div className="space-y-4">
                    <div className="camera-viewfinder relative">
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full object-cover"
                      />
                      {!scanning && !cameraError && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                          <Button
                            onClick={startCamera}
                            className="gap-2 bg-primary text-primary-foreground"
                            data-testid="start-batch-camera-btn"
                          >
                            <Camera className="w-4 h-4" />
                            Start Scanning
                          </Button>
                        </div>
                      )}
                      {loading && (
                        <div className="absolute top-4 left-4 bg-black/70 px-3 py-2 rounded-sm flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-primary" />
                          <span className="text-sm">Looking up {lastScannedCode}...</span>
                        </div>
                      )}
                      {scanning && (
                        <div className="absolute bottom-4 left-4 right-4">
                          <div className="bg-black/70 px-3 py-2 rounded-sm text-center">
                            <span className="text-sm text-green-400 flex items-center justify-center gap-2">
                              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                              Scanning... Point at barcode
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {scanning && (
                      <Button
                        variant="outline"
                        onClick={stopCamera}
                        className="w-full gap-2"
                        data-testid="stop-batch-camera-btn"
                      >
                        <StopCircle className="w-4 h-4" />
                        Stop Camera
                      </Button>
                    )}

                    {cameraError && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{cameraError}</AlertDescription>
                      </Alert>
                    )}
                  </div>
                ) : (
                  <form onSubmit={handleManualSubmit} className="space-y-4">
                    <div className="flex gap-2">
                      <Input
                        value={manualBarcode}
                        onChange={(e) => setManualBarcode(e.target.value)}
                        placeholder="Enter barcode (EAN/UPC)"
                        className="h-12 bg-secondary border-border font-mono flex-1"
                        data-testid="batch-manual-input"
                        autoFocus
                      />
                      <Button
                        type="submit"
                        disabled={loading || !manualBarcode.trim()}
                        className="h-12 px-6 bg-primary text-primary-foreground"
                        data-testid="batch-add-btn"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Enter barcode and press Enter or click + to add to queue
                    </p>
                  </form>
                )}

                {/* Default Conditions */}
                <div className="pt-4 border-t border-border">
                  <h4 className="text-sm font-medium mb-3">Default Conditions</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Vinyl</label>
                      <Select value={defaultConditionVinyl} onValueChange={setDefaultConditionVinyl}>
                        <SelectTrigger className="bg-secondary border-border" data-testid="batch-default-vinyl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CONDITION_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.value}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Cover</label>
                      <Select value={defaultConditionCover} onValueChange={setDefaultConditionCover}>
                        <SelectTrigger className="bg-secondary border-border" data-testid="batch-default-cover">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CONDITION_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.value}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {queue.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={applyDefaultConditions}
                      className="mt-3 w-full"
                      data-testid="apply-defaults-btn"
                    >
                      Apply to all items in queue
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Queue Section */}
          <div className="space-y-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-primary" />
                    Import Queue ({queue.length})
                  </span>
                  {totalValue > 0 && (
                    <span className="text-sm font-normal text-muted-foreground">
                      ~{formatCurrency(totalValue)}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {queue.length === 0 ? (
                  <div className="text-center py-12">
                    <Barcode className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground">
                      No items in queue yet.<br />
                      Scan barcodes to add albums.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {queue.map((item, index) => (
                      <div
                        key={item.id}
                        className="flex gap-3 p-3 bg-secondary/50 rounded-sm border border-border animate-fade-in"
                        data-testid={`queue-item-${index}`}
                      >
                        <div className="w-16 h-16 flex-shrink-0 rounded-sm overflow-hidden bg-secondary">
                          <img
                            src={item.cover_image || placeholderImage}
                            alt={item.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm truncate">{item.title}</h4>
                          <p className="text-xs text-muted-foreground truncate">{item.artist}</p>
                          <div className="flex flex-wrap gap-1 mt-1 text-xs text-muted-foreground">
                            {item.year && <span>{item.year}</span>}
                            {item.label && <span>• {item.label}</span>}
                          </div>
                          <div className="flex gap-2 mt-2">
                            <Select 
                              value={item.condition_vinyl} 
                              onValueChange={(v) => updateQueueItemCondition(item.id, 'condition_vinyl', v)}
                            >
                              <SelectTrigger className="h-7 text-xs w-16 bg-background">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {CONDITION_OPTIONS.map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>{opt.value}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select 
                              value={item.condition_cover} 
                              onValueChange={(v) => updateQueueItemCondition(item.id, 'condition_cover', v)}
                            >
                              <SelectTrigger className="h-7 text-xs w-16 bg-background">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {CONDITION_OPTIONS.map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>{opt.value}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFromQueue(item.id)}
                          className="text-destructive hover:text-destructive h-8 w-8 p-0"
                          data-testid={`remove-queue-item-${index}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {queue.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-border space-y-3">
                    <Button
                      onClick={handleImport}
                      disabled={importing}
                      className="w-full h-12 gap-2 bg-primary text-primary-foreground"
                      data-testid="import-all-btn"
                    >
                      {importing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Check className="w-4 h-4" />
                          Import {queue.length} Album{queue.length !== 1 ? 's' : ''} to Collection
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setQueue([])}
                      className="w-full"
                      data-testid="clear-queue-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Clear Queue
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
